import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error');
        setErrorMsg('Missing verification token. Please check the link from your email.');
        return;
      }
      try {
        await api.post('/auth/verify-email', { token });
        setStatus('success');
        await checkAuth();
      } catch (err) {
        setStatus('error');
        setErrorMsg(err.response?.data?.detail || 'This link is invalid or has expired.');
      }
    };
    verify();
  }, [token, checkAuth]);

  return (
    <div className="min-h-screen bg-sand relative overflow-hidden" data-testid="verify-email-page">
      <div className="noise-overlay" />
      <div className="ethereal-glow-gold animate-drift" style={{ top: '-150px', right: '-100px' }} />
      <div className="ethereal-glow-moss animate-drift-slow" style={{ bottom: '-150px', left: '-100px' }} />

      <div className="relative z-10 flex min-h-screen items-center justify-center p-6">
        <div className="max-w-md w-full text-center animate-fade-up">
          {status === 'verifying' && (
            <div data-testid="verify-loading">
              <div className="relative w-20 h-20 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full bg-gold/15 animate-breathe" />
                <div className="absolute inset-3 rounded-full bg-gold/20 animate-breathe flex items-center justify-center" style={{ animationDelay: '0.4s' }}>
                  <Loader2 size={24} className="text-gold animate-spin" />
                </div>
              </div>
              <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase">Verifying your email...</p>
            </div>
          )}

          {status === 'success' && (
            <div data-testid="verify-success">
              <div className="relative w-20 h-20 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full bg-gold/15 animate-breathe" />
                <div className="absolute inset-3 rounded-full bg-gold/25 flex items-center justify-center">
                  <CheckCircle2 size={28} className="text-gold" />
                </div>
              </div>
              <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-3">Anchored</p>
              <h1 className="font-serif text-4xl font-light text-dark mystical-heading mb-3">Email verified</h1>
              <p className="font-sans text-base text-text-muted mb-8 leading-relaxed">
                Your account is fully anchored. The Practice awaits.
              </p>
              <Button
                onClick={() => navigate('/', { replace: true })}
                data-testid="verify-continue-button"
                className="h-12 px-8 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-xs tracking-[0.25em] uppercase btn-hover"
              >
                Enter the Practice
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div data-testid="verify-error">
              <AlertCircle size={44} className="text-gold mx-auto mb-5" />
              <h1 className="font-serif text-3xl sm:text-4xl font-light text-dark mb-4 mystical-heading">Verification failed</h1>
              <p className="font-sans text-sm text-text-muted leading-relaxed mb-8">{errorMsg}</p>
              <Link
                to="/"
                data-testid="verify-return-link"
                className="inline-flex h-12 items-center justify-center px-8 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-xs tracking-[0.25em] uppercase btn-hover"
              >
                Continue to App
              </Link>
              <p className="font-sans text-xs text-text-muted mt-5">
                You can request a fresh link from the banner on your dashboard.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
